
import Vue from "vue";
import MultiUploadBlock, {UploadState} from "../../common/upload/multi-upload-block";
import {ImageUploadResult} from "../../common/image/edit-image-dialog";
import {IFileDescriptor} from "@ponte/file-upload-js";
import {SlimSelectOption} from "src/components/common/vue-slim-select/vue-slim-select";
import ImageSizePicker, {convertPickedSizesToImageSizes, PickedImageSize, SupportedFormat} from "./image-size-picker/image-size-picker.vue";
import AndroidImageSizePicker, {defaultAndroidOutputFormatOptions} from "./image-size-picker/android-image-size-picker.vue";
import IosImageSizePicker, {defaultIOSOutputFormatOptions} from "./image-size-picker/ios-image-size-picker.vue";
import WebImageSizePicker, {defaultWebOutputFormatOptions} from "./image-size-picker/web-image-size-picker.vue";
import ReactNativeImageSizePicker, {defaultReactNativeOutputFormatOptions} from "./image-size-picker/react-native-image-size-picker.vue";
import TeamAndProjectTargetPicker from "../../../components/common/target-picker/team-and-project-target-picker.vue";
import ErrorHandler from "../../../services/error-handler";
import RespressoApi from "../../../api/respresso-api";
import {DialogBuilder, NotificationBuilder} from "../../common/dialog/dialog";
import {translate} from "../../../main";
import {TeamAndProjectTarget} from "../../common/target-picker/team-and-project-target-picker.vue";
import {BulkImportTarget, TeamMeta} from "respresso";
import {teamModule} from "../../../store/modules/team";
import userService from "../../../services/user-service";
import AccessService from "../../../services/access-service";
import CreateProjectDialog from "../../common/create-project/dialog/create-project-dialog";
import StorageService from "../../../services/storage-service";

interface ImageConverterRequest {
  files: ImageConverterFileData[];
  config: ImageConverterConfig;
}

interface ImageConverterFileData {
  id: string;
  key?: string; // Allow rename?
  // Additional metadata?
}

interface ImageConverterConfig {
  android?: ImageConverterAndroidConfig;
  ios?: ImageConverterIOSConfig;
  web?: ImageConverterWebConfig;
  reactNative?: ImageConverterReactNativeConfig;
  scss?: ImageConverterScssConfig;
}

interface ImageConverterFile {
  id: string;
  key?: string; // Allow rename?
  // Additional metadata?
}

interface ImageConverterPlatformSizesConfig {
  sizes?: PickedImageSize[];
}

interface ImageConverterAndroidConfig extends ImageConverterPlatformSizesConfig {
}

type IosVectorFormat = "pdf" | "svg"

interface ImageConverterIOSConfig extends ImageConverterPlatformSizesConfig {
  vectorFormat?: IosVectorFormat
}

interface ImageConverterWebConfig extends ImageConverterPlatformSizesConfig {
}

interface ImageConverterReactNativeConfig extends ImageConverterPlatformSizesConfig {
}

interface ImageConverterScssConfig {
  fileSizeLimit: number;
}

function defaultSizes(formats: SupportedFormat[]): PickedImageSize[] {
  return [{
    base: "original",
    format: formats[0].key,
  }];
}

export function downloadConversionResult(fileId: string, fileName: string = "respresso.zip") {
  const a = document.createElement('a');
  a.setAttribute('href', `/api/ui/tools/convert/result/${fileId}`);
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default Vue.extend({
  name: "image-converter",
  components: {
    ImageSizePicker,
    AndroidImageSizePicker,
    IosImageSizePicker,
    WebImageSizePicker,
    ReactNativeImageSizePicker,
    TeamAndProjectTargetPicker
  },
  data() {
    return {
      uploaderTypes: ['svg', 'drawable', 'pdf', 'png', 'jpg', 'webp'],
      acceptFiles: ['.svg', '.xml', '.pdf', '.png', '.jpg', '.webp'],
      uploadState: 'empty' as UploadState,
      images: [] as ImageUploadResult[],
      platforms: ["android", "ios", "web", "reactnative", "scss"],
      selectedPlatforms: ["android", "ios"] as string[],
      selectedImageIds: [] as string[],
      surpassedImageLimit: false,
      imageLimit: 5,
      androidSizes: defaultSizes(defaultAndroidOutputFormatOptions),
      iosSizes: defaultSizes(defaultIOSOutputFormatOptions),
      webSizes: defaultSizes(defaultWebOutputFormatOptions),
      reactNativeSizes: defaultSizes(defaultReactNativeOutputFormatOptions),
      iosVectorFormat: "pdf" as IosVectorFormat,
      scssSizeLimit: `${8 * 1024}` as string,
      uploaderParams: { "tool": "image-converter", "category": "image" },
      importTarget: undefined as TeamAndProjectTarget | undefined,
      converted: false
    }
  },
  computed: {
    uploader(): MultiUploadBlock {
      return this.$refs.uploadBlock as MultiUploadBlock;
    },
    team(): TeamMeta | null {
      return teamModule.team;
    },
    isTeamAdmin(): boolean {
      return AccessService.hasTeamRole('ADMIN', this.team);
    },
    canUseProjectImport(): boolean {
      this.converted; //
      const isOnboarding = StorageService.getIsSignedUpInThisBrowser() && StorageService.getImageConverterLastUsage() === null;
      return !isOnboarding;
    },
    scssSizeLimitOptions(): SlimSelectOption[] {
      const options: SlimSelectOption[] = [
        {
          value: `-1`,
          text: "Unlimited"
        },
        {
          value: `${4 * 1024}`,
          text: "4 KB"
        },
        {
          value: `${8 * 1024}`,
          text: "8 KB"
        },
        {
          value: `${32 * 1024}`,
          text: "32 KB"
        },
        {
          value: `${128 * 1024}`,
          text: "128 KB"
        },
        {
          value: `${1024 * 1024}`,
          text: "1 MB"
        }
      ];
      return options.map(it => {
        if (it.value === this.scssSizeLimit) {
          it.selected = true;
        }
        return it;
      })
    }
  },
  created() {
    if(!teamModule.team) {
      userService.getOrLoadTeams(true).then(teams => {
        this.isTeamAdmin; // Force update computed property
      })
    }
  },
  mounted() {
    const uploader = this.uploader;
    this.uploadState = uploader.getState();
    uploader.onUploadSucceeded(async (upload) => {
      const result = await upload.uploader.uploadResult;
      await this.handleUploadedFile(result as ImageUploadResult);
    });
  },
  methods: {
    uploadStateChanged: function(state: UploadState) {
      this.uploadState = state;
    },
    getKeyFromFileName: function(fileName: string): string {
      let key = fileName;
      let extensionStartIndex = fileName.lastIndexOf(".");
      if (extensionStartIndex >= 0) {
        key = fileName.substring(0, extensionStartIndex);
      }
      return key;
    },
    handleUploadedFile: async function(uploadedFile: ImageUploadResult) {
      const newKey = this.getKeyFromFileName(uploadedFile.name);
      const imagesToRemove = this.images.filter(it => {
        let existingKey = this.getKeyFromFileName(it.name);
        return existingKey == newKey;
      })
      imagesToRemove.forEach(image => {
        NotificationBuilder.warning(translate('tools.imageConverter.upload.prevImageWithSameNameRemoved', { name: image.name }));
        this.removeImage(image.fileId);
      });
      this.images.unshift(uploadedFile);
      this.selectImage(uploadedFile.fileId);
    },
    imagePreviewUrl: function(uploadedFile: ImageUploadResult) {
      return `/api/ui/file/tmp/preview/${uploadedFile.fileId}`;
    },
    uploadSelectedFile: async function(file: IFileDescriptor): Promise<boolean> {
      this.uploader.submitUpload(file.file, this.uploaderParams);
      return true; // Return true, to indicate that file is handled to avoid duplicated upload
    },
    uploadFiles: function(file?: IFileDescriptor): void {
      const uploader = this.uploader;
      if (uploader) {
        if (file && file.file) {
          uploader.submitUpload(file.file, this.uploaderParams);
        } else {
          uploader.triggerFileSelect();
        }
      }
    },
    invertPlatformSelection: function(platform: string) {
      if (this.selectedPlatforms.includes(platform)) {
        this.selectedPlatforms = this.selectedPlatforms.filter(it => it !== platform);
      } else {
        this.selectedPlatforms.push(platform);
      }
    },
    selectPlatform: function(platform: string) {
      if (!this.selectedPlatforms.includes(platform)) {
        this.selectedPlatforms.push(platform);
      }
    },
    selectImage: function(fileId: string) {
      const file = this.images.find(it => it.fileId === fileId);
      if (file) {
        if (!this.selectedImageIds.includes(fileId)) {
          this.selectedImageIds.unshift(fileId);
          while (this.selectedImageIds.length > this.imageLimit) {
            this.selectedImageIds.pop();
            this.surpassedImageLimit = true;
          }
        }
      } else {
        console.log(`Image not found with file id: ${fileId}`);
      }
    },
    deselectImage: function(fileId: string) {
      this.selectedImageIds = this.selectedImageIds.filter(it => it !== fileId);
    },
    removeImage: function(fileId: string) {
      this.deselectImage(fileId);
      this.images = this.images.filter(it => it.fileId !== fileId);
    },
    generateSingleSelectOptions: function(options: { [key: string]: string }, selection: string): SlimSelectOption[] {
      const slimSelectOptions: SlimSelectOption[] = [];
      for (let optionsKey in options) {
        if (options.hasOwnProperty(optionsKey)) {
        }
        slimSelectOptions.push({
          value: optionsKey,
          text: options[optionsKey],
          selected: selection === optionsKey,
        })
      }
      return slimSelectOptions;
    },
    convert: async function() {
      if (this.selectedImageIds.length > 0 && this.selectedPlatforms.length > 0) {
        const request: ImageConverterRequest = {
          files: this.selectedImageIds.map((fileId) => {
            const file = this.images.find(it => it.fileId === fileId);
            let key = fileId;
            if (file) {
              const name = file.name;
              const index = name.lastIndexOf(".")
              // Empty key is bad, don't allow 0 index
              if (index > 0) {
                key = name.substring(0, index);
              }
            }
            return {
              id: fileId,
              key: key,
            }
          }),
          config: {
            android: this.selectedPlatforms.includes("android") ? {
              sizes: this.androidSizes
            } : undefined,
            ios: this.selectedPlatforms.includes("ios") ? {
              sizes: this.iosSizes,
              vectorFormat: this.iosVectorFormat,
            } : undefined,
            web: this.selectedPlatforms.includes("web") ? {
              sizes: this.webSizes
            } : undefined,
            reactNative: this.selectedPlatforms.includes("reactnative") ? {
              sizes: this.reactNativeSizes
            } : undefined,
            scss: this.selectedPlatforms.includes("scss") ? {
              fileSizeLimit: Number.parseInt(this.scssSizeLimit, 10),
            } : undefined,
          }
        };
        const result = await ErrorHandler.tryRequest(() => RespressoApi.callImageConverter(request), {
          loadingScreen: true
        });
        // console.log(result)
        if (result) {
          StorageService.setImageConverterLastUsage();
          this.converted = true;
          downloadConversionResult(result.fileId, result.name);
        }
      }
      // error handling? -> can't press the button
    },
    targetChanged: function(target: TeamAndProjectTarget) {
      //console.log(target)
      this.importTarget = target;
    },
    startProjectWithImages: async function() {
      const team = teamModule.team;
      if(team) {
        const controller = DialogBuilder.createVueDialog(CreateProjectDialog, {
          propsData: {
            dialogConfig: {
              team: team,
            }
          },
        });
        const projectId = await controller.closeResult;
        if(projectId){
          this.importTarget = {team: team.id, project: projectId, type: "user"};
          await this.importImages();
        }
      }
    },
    importImages: async function() {
      if (this.importTarget) {
        const target = this.importTarget;
        const pendingImports = await ErrorHandler.tryRequest(() => RespressoApi.getPendingImports(target.team, target.project), {
          loadingScreen: true,
        });
        let desiredTarget: BulkImportTarget | undefined = undefined;
        const fileData: any = {};
        this.platforms.forEach(platform => {
          if (platform === "android") {
            fileData["android"] = {
              sizes: this.selectedPlatforms.includes(platform) ? convertPickedSizesToImageSizes(this.androidSizes) : []
            }
          } else if (platform === "ios") {
            fileData["ios"] = {
              sizes: this.selectedPlatforms.includes(platform) ? convertPickedSizesToImageSizes(this.iosSizes) : []
            }
          } else if (platform === "web") {
            fileData["web"] = {
              sizes: this.selectedPlatforms.includes(platform) ? convertPickedSizesToImageSizes(this.webSizes) : []
            }
          }
        })
        if (pendingImports && pendingImports.imports && pendingImports.imports.targets && pendingImports.imports.targets.length > 0) {
          const availableImageImports = pendingImports.imports.targets.filter(it => it.category === "image");
          if (availableImageImports.length === 1) {
            const target = availableImageImports[0];
            const matchingFiles = target.files.filter(it => this.images.some(image => image.fileId === it.id));
            if (matchingFiles.length > 0) {
              matchingFiles.forEach(file => {
                if (!file.config) {
                  file.config = {}
                }
                file.config["fileData"] = fileData;
              })
              target.files = matchingFiles;
              desiredTarget = target
            }
          }
        }
        if (desiredTarget != undefined) {
          const finalTarget = desiredTarget;
          const result = await ErrorHandler.tryRequest(() => {
            return RespressoApi.bulkImport(target.team, target.project, {
              targets: [finalTarget]
            });
          }, {loadingScreen: true, loadingMessage: "Importing images... It may take a while."});
          if(result !== null) {
            NotificationBuilder.success("All current files added to your project.")
            this.$router.push({
              name: 'imageResource',
              params: {
                teamId: target.team,
                projectId: target.project,
                versionNumber: desiredTarget.version,
              },
            });
          }
        } else {
          NotificationBuilder.error("Failed to auto import your current image files. Try to go to the target project dashboard and pick pending imports to import them manually.")
        }
      }
    }
  }
})
